import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { setSession } from "../../auth/session.js";
import { AuthLayout, Button, ErrorNotice, Field, Input, Select } from "../../components/form.js";
import { LegalConsent } from "../../components/legal-consent.js";
import { useFeatures } from "../../features.js";

export function SignupPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { domain } = useSearch({ from: "/public/signup" });
  const features = useFeatures();
  const [form, setForm] = useState({
    tenantName: "",
    displayName: "",
    email: "",
    password: "",
    locale: (i18n.language === "tr" ? "tr" : "en") as "en" | "tr",
  });
  const update = (key: keyof typeof form) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const signup = useMutation({
    mutationFn: () => api.auth.signup(form),
    onSuccess: (session) => {
      setSession(queryClient, session);
      void i18n.changeLanguage(session.user.locale);
      if (domain) {
        void navigate({ to: "/admin/domains/checkout", search: { domain, years: 1 } });
      } else {
        void navigate({ to: "/mail" });
      }
    },
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    signup.mutate();
  };

  return (
    <AuthLayout
      title={t("auth.signup.title")}
      subtitle={domain ? t("auth.signup.forDomain", { domain }) : t("auth.signup.subtitle")}
    >
      {!features.loaded || features.signup ? null : (
        <p className="mb-4 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent">
          {t("auth.signup.closed")}
        </p>
      )}
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field label={t("auth.fields.tenantName")} hint={t("auth.signup.tenantHint")}>
          <Input
            required
            maxLength={200}
            value={form.tenantName}
            onChange={(e) => update("tenantName")(e.target.value)}
          />
        </Field>
        <Field label={t("auth.fields.displayName")}>
          <Input
            required
            autoComplete="name"
            maxLength={200}
            value={form.displayName}
            onChange={(e) => update("displayName")(e.target.value)}
          />
        </Field>
        <Field label={t("auth.fields.email")}>
          <Input
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={(e) => update("email")(e.target.value)}
          />
        </Field>
        <Field label={t("auth.fields.password")} hint={t("auth.fields.passwordHint")}>
          <Input
            type="password"
            required
            minLength={10}
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => update("password")(e.target.value)}
          />
        </Field>
        <Field label={t("auth.fields.locale")}>
          <Select value={form.locale} onChange={(e) => update("locale")(e.target.value)}>
            <option value="en">English</option>
            <option value="tr">Türkçe</option>
          </Select>
        </Field>
        <ErrorNotice error={signup.error} />
        <Button type="submit" disabled={signup.isPending}>
          {signup.isPending ? t("common.loading") : t("auth.signup.submit")}
        </Button>
        <LegalConsent i18nKey="auth.signup.agree" />
        <p className="text-center text-sm text-ink-2">
          {t("auth.signup.haveAccount")}{" "}
          <Link to="/login" className="text-accent underline-offset-2 hover:underline">
            {t("auth.signup.signIn")}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
